/**
 * Google Calendar v3 REST calls. No React imports.
 *
 * `googleapis.com` echoes the exact request origin in
 * `Access-Control-Allow-Origin` and its preflight passes, so the
 * `Authorization: Bearer` header (which does force a preflight) is fine.
 *
 * Everything goes through `fetchJson`, so nothing here rejects; a 401 comes
 * back as a status the caller can turn into "your session expired" rather
 * than an unhandled rejection in the console.
 */

import { fetchJson } from '../http';
import {
  colorMap,
  normalizeEvent,
  selectableCalendars,
  windowTimeMax,
  windowTimeMin,
  type CalendarEvent,
  type GoogleCalendarListEntry,
  type GoogleEvent,
} from './events';

const BASE = 'https://www.googleapis.com/calendar/v3';

/** A read storm of 30 calendars on every poll is not "at a glance". */
const MAX_CALENDARS = 8;
/** The panel shows at most 8 rows; 50 per calendar is already generous. */
const MAX_EVENTS_PER_CALENDAR = 50;

interface CalendarListResponse {
  readonly items?: readonly GoogleCalendarListEntry[];
}

interface EventsResponse {
  readonly items?: readonly GoogleEvent[];
}

function auth(accessToken: string): RequestInit {
  return { headers: { Authorization: `Bearer ${accessToken}` } };
}

export function calendarListUrl(): string {
  const q = new URLSearchParams({
    minAccessRole: 'reader',
    // `fields` keeps the response small and makes the shape explicit.
    fields: 'items(id,summary,backgroundColor,primary,selected,deleted)',
    maxResults: '250',
  });
  return `${BASE}/users/me/calendarList?${q.toString()}`;
}

export function eventsUrl(calendarId: string, from: Date = new Date()): string {
  const q = new URLSearchParams({
    // singleEvents expands recurrences into individual instances; orderBy is
    // only legal together with it.
    singleEvents: 'true',
    orderBy: 'startTime',
    timeMin: windowTimeMin(from),
    timeMax: windowTimeMax(from),
    maxResults: String(MAX_EVENTS_PER_CALENDAR),
    fields: 'items(id,status,summary,start,end)',
  });
  return `${BASE}/calendars/${encodeURIComponent(calendarId)}/events?${q.toString()}`;
}

export interface CalendarLoad {
  readonly events: readonly CalendarEvent[];
  /** id -> backgroundColor, for the per-event colour bar. */
  readonly colors: ReadonlyMap<string, string>;
}

export type CalendarOutcome =
  | { readonly ok: true; readonly value: CalendarLoad }
  | { readonly ok: false; readonly error: string; readonly unauthorized: boolean };

/**
 * One calendarList call + one events call per visible calendar, in parallel.
 * Never throws.
 *
 * A single calendar failing is tolerated — the rest of the day still renders.
 * Only a failure to list calendars at all, or a 401, is fatal.
 */
export async function loadCalendar(
  accessToken: string,
  from: Date = new Date(),
): Promise<CalendarOutcome> {
  const init = auth(accessToken);

  const list = await fetchJson<CalendarListResponse>(calendarListUrl(), init);
  if (!list.ok) {
    return {
      ok: false,
      error: describeError(list.status, list.error),
      unauthorized: list.status === 401 || list.status === 403,
    };
  }

  const entries = list.data.items ?? [];
  const colors = colorMap(entries);
  const visible = selectableCalendars(entries, MAX_CALENDARS);

  const responses = await Promise.all(
    visible.map(async (calendar) => {
      const id = calendar.id;
      if (id === undefined) return null;
      const res = await fetchJson<EventsResponse>(eventsUrl(id, from), init);
      if (!res.ok) return { id, unauthorized: res.status === 401, items: [] as readonly GoogleEvent[] };
      return { id, unauthorized: false, items: res.data.items ?? [] };
    }),
  );

  // A 401 on every calendar means the token died mid-flight, not that the
  // calendars are empty — surface it so the panel can re-prompt.
  const attempted = responses.filter((r) => r !== null);
  if (attempted.length > 0 && attempted.every((r) => r.unauthorized)) {
    return { ok: false, error: 'Your Google session expired.', unauthorized: true };
  }

  const events: CalendarEvent[] = [];
  for (const response of attempted) {
    const color = colors.get(response.id) ?? null;
    for (const raw of response.items) {
      const normalized = normalizeEvent(raw, response.id, color);
      if (normalized !== null) events.push(normalized);
    }
  }

  return { ok: true, value: { events, colors } };
}

function describeError(status: number | null, fallback: string): string {
  if (status === 401) return 'Your Google session expired.';
  if (status === 403) return 'Calendar access was denied for this account.';
  if (status === 429) return 'Google rate limit reached.';
  return fallback;
}
